import React, { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, TrendingUp } from "lucide-react";

// =============================================================================
// AGODA PICKUP TRACKER
// =============================================================================
// One row per daily capture ("as of" a date/time), one column per future
// stay date within that capture's lookout window. Reading DOWN a column
// shows how bookings "pick up" for that stay date as it approaches — that's
// what this component visualizes, with a pickup delta badge on every cell
// and a focused trend panel for whichever stay date is selected.
//
// LIVE DATA ONLY: on mount this calls GET /api/agodapickup/summary (see
// agodaPickupRoutes.js / agodaPickupAthenaService.js). There is no
// fabricated fallback data -- if the route isn't mounted, returns no
// rows, or the request fails, the component shows an honest loading or
// empty/error state instead, so nothing shown here is ever made up.
//
// JSX + CSS are kept in this one file (see the <style> block at the bottom
// of the render) rather than a separate .css import, per request.
// =============================================================================

const PLATFORM_OPTIONS = ["Agoda"]; // more OTAs can be added once scraped
const LOCATION_OPTIONS = ["Rishikesh", "Mussoorie", "Ayodhya"];

// ---- date helpers ----
function parseISO(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function addDays(dateStr, n) {
  const d = parseISO(dateStr);
  d.setDate(d.getDate() + n);
  return toISO(d);
}
function formatShort(dateStr) {
  return parseISO(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
function formatWithWeekday(dateStr) {
  return parseISO(dateStr).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
}
function formatCapturedLabel(capturedAt) {
  if (!capturedAt) return "—";
  const d = new Date(capturedAt);
  if (Number.isNaN(d.getTime())) return capturedAt;
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

// The earliest captures (22-30 Sept) came from the first, partial test runs
// (some stay dates show 99% sold out on a few hundred hotels), so the matrix
// starts at 1 Oct by default. The "Stay dates from" box lets you move this;
// clearing it shows everything.
const DEFAULT_FROM_STAY_DATE = "2026-10-01";

// How many stay-date columns the summary cards and the headline cover. 1 = the
// nearest night in the latest capture, which is normally today's own night.
// Today-only is the default because it needs room-level data for ONE night, not
// seven, so a detail scrape of a single date is enough to fill every card.
const DEFAULT_WINDOW_DAYS = 1;

// A night listed with fewer hotels than this is almost certainly an incomplete
// sample (a normal night lists ~1,000-1,270). The header row flags those in amber.
const THIN_HOTEL_COUNT = 500;

// What share of the hotels Agoda says exist a night's count covers. A normal full scrape reaches
// ~76-86% (paging overlaps), so only a clearly lower share is flagged.
const COVERAGE_WARN = 0.7;

// A number from a row field, or null when the field is absent / NULL / empty / not a number --
// never 0, so a missing value can't pass for a real one.
function numOrNull(v) {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

// "Rooms available" for nights that were NOT scraped in detail is an ESTIMATE:
// the listing's rooms-left total (cheapest offer per hotel) times this ratio.
// Measured 2026-10-06 on four nights 7-31 days out (12, 16, 25 Oct and 1 Nov):
// detail total / listing total = 2.51, 2.46, 2.47, 2.50 -> mean 2.49, and predicting
// each night from the others was off by 1.0% on average, 1.3% at worst. Closer-in
// nights were noisier (2.02, 2.37, 2.50 for 0-2 days out), so those are flagged as
// less certain. Nothing beyond 31 days out has been checked. Re-measure with
// rooms_ratio_check.py as more detail nights accumulate and update this number.
const ROOMS_ESTIMATE_RATIO = 2.49;
const ROOMS_LOW_CONFIDENCE_DAYS = 7;

// How many nights the "Top nights by rooms available" table on the right lists.
const TOP_NIGHTS = 7;

// Keeps only rows whose stay_date is on/after `fromStayDate` (YYYY-MM-DD;
// plain string comparison is correct for ISO dates). Done BEFORE the rows are
// grouped into captures, so a capture whose stay dates are all too old simply
// disappears instead of showing up as an empty row.
function filterRowsFromStayDate(rows, fromStayDate) {
  if (!fromStayDate) return rows;
  return rows.filter((r) => String(r.stay_date || "") >= fromStayDate);
}

// Rooms available for one cell: the real count if that night was scraped in
// detail, otherwise an estimate from the listing's rooms-left total.
// Returns null when neither exists.
function roomsForCell(cell, stayDate, asOfDate) {
  if (!cell) return null;
  if (typeof cell.availableRooms === "number" && !Number.isNaN(cell.availableRooms)) {
    return { value: cell.availableRooms, estimated: false, lowConfidence: false, listing: cell.listingRooms };
  }
  if (typeof cell.listingRooms === "number" && !Number.isNaN(cell.listingRooms)) {
    const daysOut = Math.round((parseISO(stayDate).getTime() - parseISO(asOfDate).getTime()) / 86400000);
    return {
      value: Math.round(cell.listingRooms * ROOMS_ESTIMATE_RATIO),
      estimated: true,
      lowConfidence: daysOut < ROOMS_LOW_CONFIDENCE_DAYS,
      listing: cell.listingRooms,
    };
  }
  return null;
}

// Turns the flat rows GET /api/agodapickup/summary returns (one row per
// platform+location+captured_at+stay_date) into the grouped-by-capture
// shape the rest of this component renders.
function buildSnapshotsFromRows(rows) {
  const byCapturedDate = new Map();
  rows.forEach((r) => {
    const capturedDate = String(r.captured_at || "").slice(0, 10);
    const stayDate = r.stay_date;
    if (!capturedDate || !stayDate) return;
    if (!byCapturedDate.has(capturedDate)) {
      byCapturedDate.set(capturedDate, {
        asOfDate: capturedDate,
        asOfLabel: formatCapturedLabel(r.captured_at),
        propertyCount: 0,
        totalBeds: 0,
        cells: {},
      });
    }
    const snap = byCapturedDate.get(capturedDate);
    const propertyCount = Number(r.property_count) || 0;
    const soldOutCount = Number(r.sold_out_count) || 0;
    const scarcityPct = Number(r.scarcity_pct) || 0;
    // Real inventory metrics -- counted ONLY from the room-only (EP, no
    // breakfast) rate plan, deduped per hotel+room-category by lowest
    // price (see aggregate_and_upload_pickup.py's compute_ep_room_metrics).
    const availableRooms = (r.available_rooms_total !== undefined && r.available_rooms_total !== null)
      ? Number(r.available_rooms_total)
      : null;
    const soldOutPropertiesEp = (r.sold_out_properties_ep !== undefined && r.sold_out_properties_ep !== null)
      ? Number(r.sold_out_properties_ep)
      : null;
    // The same stay date can arrive twice inside one capture day: once from a
    // detail run (full room counts) and once from a listing-only "lite" run
    // (room counts empty, because the listing can't supply them). Rows come
    // in capture-time order, so without this a later lite row would overwrite
    // a detail row and blank its room cards (seen on 16 Oct, 25 Oct and 1 Nov).
    // The two agree on sold-out status to ~99.7%, so keeping the fuller detail
    // cell loses nothing; a detail row arriving AFTER a lite one still wins.
    // listing_rooms_total only exists on listing-only rows (and only once the Athena table has the
    // column), so absent/NULL/empty all mean "not available" -- never 0.
    const listingRooms = (r.listing_rooms_total !== undefined && r.listing_rooms_total !== null && r.listing_rooms_total !== ""
      && !Number.isNaN(Number(r.listing_rooms_total)))
      ? Number(r.listing_rooms_total)
      : null;
    // Optional listing-row field (only once the Athena table has the column): how many hotels Agoda
    // says exist, so the hotel count can show how much of Agoda's list it covers.
    const claimed = numOrNull(r.claimed_total);
    const existing = snap.cells[stayDate];
    if (existing && existing.soldOutPropertiesEp !== null && soldOutPropertiesEp === null) {
      // keep the fuller detail cell, but remember the listing's figures if this lite row has them
      if (existing.listingRooms === null && listingRooms !== null) existing.listingRooms = listingRooms;
      if (existing.claimed == null && claimed !== null) existing.claimed = claimed;
      return;
    }
    snap.cells[stayDate] = {
      occupiedBeds: soldOutCount,
      occupancyPct: scarcityPct,
      totalBeds: propertyCount,
      availableRooms,
      soldOutPropertiesEp,
      listingRooms: listingRooms !== null ? listingRooms : (existing ? existing.listingRooms : null),
      claimed: claimed !== null ? claimed : (existing && existing.claimed != null ? existing.claimed : null),
    };
    // property_count is the same for every row of one capture in
    // principle -- just keep the latest non-zero value seen. (Field is
    // still named totalBeds/occupiedBeds internally -- these hold real
    // sold-out-hotel counts now, not bed counts; Agoda never exposed true
    // room inventory, which is why the metric became sold_out_count /
    // scarcity_pct in the first place.)
    if (propertyCount) snap.propertyCount = propertyCount;
    if (propertyCount) snap.totalBeds = propertyCount;
  });
  return Array.from(byCapturedDate.values()).sort((a, b) => a.asOfDate.localeCompare(b.asOfDate));
}

// Sold rooms for one cell, measured against the market's maximum room count (the Max Occupancy of
// Market figure). sold = max - available (never below 0), sold % = sold / max. Null when the cell has
// no rooms figure or there is no maximum yet. `overMax` flags a figure above the maximum (only a less
// certain near-night estimate can be: those are kept out of the maximum), shown as 0 sold.
function soldForCell(cell, stayDate, asOfDate, maxRooms) {
  const rooms = roomsForCell(cell, stayDate, asOfDate);
  if (!rooms || typeof maxRooms !== "number" || !(maxRooms > 0)) return null;
  const sold = Math.max(0, maxRooms - rooms.value);
  return {
    available: rooms.value, sold, pct: (100 * sold) / maxRooms, max: maxRooms,
    estimated: rooms.estimated, lowConfidence: rooms.lowConfidence, listing: rooms.listing,
    overMax: rooms.value > maxRooms,
  };
}

function occupancyClass(pct) {
  if (pct === null || pct === undefined) return "ap-cell-empty";
  if (pct >= 70) return "ap-cell-high";
  if (pct >= 35) return "ap-cell-mid";
  return "ap-cell-low";
}

// Average scarcity % across a snapshot's own next N available stay-date
// columns (same windowing idea as the summary cards, but reusable for ANY
// snapshot, not just the latest one -- needed to build an honest
// "compared to N days ago" headline rather than just showing today's
// number with nothing to measure it against.
function avgScarcityForSnapshot(snapshot, stayDateColumns, windowSize = 7) {
  if (!snapshot) return null;
  const cols = stayDateColumns.filter((d) => snapshot.cells[d]).slice(0, windowSize);
  if (cols.length === 0) return null;
  const sum = cols.reduce((s, d) => s + snapshot.cells[d].occupancyPct, 0);
  return sum / cols.length;
}

// Finds the snapshot whose asOfDate is closest to (latest - targetDaysAgo),
// among snapshots strictly before the latest one. Real capture data has
// gaps (a date might not have been scraped), so this picks the CLOSEST
// match rather than requiring an exact day, and reports back how many
// days ago that comparison point actually was so the headline can be
// honest about it instead of silently assuming exactly 7 days.
function findComparisonSnapshot(snapshots, latestDate, targetDaysAgo) {
  const candidates = snapshots.filter((s) => s.asOfDate < latestDate);
  if (candidates.length === 0) return null;
  const targetTime = addDays(latestDate, -targetDaysAgo);
  let best = candidates[0];
  let bestDiff = Math.abs(parseISO(best.asOfDate).getTime() - parseISO(targetTime).getTime());
  for (const s of candidates) {
    const diff = Math.abs(parseISO(s.asOfDate).getTime() - parseISO(targetTime).getTime());
    if (diff < bestDiff) {
      best = s;
      bestDiff = diff;
    }
  }
  const actualDaysAgo = Math.round(
    (parseISO(latestDate).getTime() - parseISO(best.asOfDate).getTime()) / 86400000
  );
  return { snapshot: best, actualDaysAgo };
}

function formatBeds(n) {
  // Confirmed necessary (2026-10-01): a live production crash traced
  // back to this function receiving `undefined` (not `null`) for an
  // older historical row predating the EP-specific metrics, which
  // slipped past an "!== null" guard elsewhere (undefined !== null is
  // TRUE in JS, so that check didn't actually exclude it). With no
  // error boundary around this component, that one bad value crashed
  // the ENTIRE dashboards app to a blank page, not just this widget.
  // Making this function itself defensive means one row of
  // incomplete/legacy data can never do that again.
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN");
}

function TrendPanel({ stayDate, snapshots }) {
  const series = useMemo(() => {
    return snapshots
      .filter((s) => s.cells[stayDate])
      .map((s) => ({ asOfLabel: s.asOfLabel, ...s.cells[stayDate] }));
  }, [snapshots, stayDate]);

  if (series.length === 0) {
    return <div className="ap-trend-empty">No captures cover this stay date yet.</div>;
  }

  const maxPct = Math.max(...series.map((s) => s.occupancyPct), 1);
  const first = series[0];
  const last = series[series.length - 1];
  const totalGrowth = last.occupiedBeds - first.occupiedBeds;

  return (
    <div className="ap-trend-panel">
      <div className="ap-trend-header">
        <div>
          <div className="ap-trend-title">Pickup for {formatWithWeekday(stayDate)}</div>
          <div className="ap-trend-subtitle">
            {series.length} capture{series.length === 1 ? "" : "s"} · {formatBeds(first.occupiedBeds)} →{" "}
            {formatBeds(last.occupiedBeds)} sold out
          </div>
        </div>
        {series.length > 1 && (
          <span className={`ap-growth-badge ${totalGrowth >= 0 ? "ap-growth-up" : "ap-growth-down"}`}>
            {totalGrowth >= 0 ? "▲" : "▼"} {formatBeds(Math.abs(totalGrowth))} sold out since first capture
          </span>
        )}
      </div>
      <div className="ap-trend-bars">
        {series.map((point) => (
          <div key={point.asOfLabel} className="ap-trend-row">
            <div className="ap-trend-row-label">{point.asOfLabel}</div>
            <div className="ap-trend-row-track">
              <div
                className={`ap-trend-row-fill ${occupancyClass(point.occupancyPct)}`}
                style={{ width: `${(point.occupancyPct / maxPct) * 100}%` }}
              />
            </div>
            <div className="ap-trend-row-value">
              {point.occupancyPct.toFixed(1)}%
              <span className="ap-trend-row-beds">{formatBeds(point.occupiedBeds)} sold out</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, tooltip }) {
  return (
    <div className="ap-summary-card" title={tooltip}>
      <div className="ap-summary-value">{value}</div>
      <div className="ap-summary-label">{label}</div>
      {sub && <div className="ap-summary-sub">{sub}</div>}
    </div>
  );
}

// The hero: one plain sentence stating where things stand right now and
// how that compares to a week ago, before any table or filter asks the
// reader to do their own interpreting. Built entirely from the EP
// (room-only rate) metric, since that's the one that's actually
// trustworthy -- it excludes hotels whose page simply failed to load,
// unlike the older sold_out_count figure, which conflates the two.
function HeadlineInsight({ latest, comparison, stayDateColumns, windowDays = 7, location = "Rishikesh" }) {
  if (!latest) return null;
  // Confirmed buggy in production (2026-10-01): this used to SUM
  // soldOutPropertiesEp across up to 7 DIFFERENT stay-date columns, then
  // divide by a SINGLE day's property count -- that produced "172%
  // sold out" on real data, since the same hotel can legitimately be
  // sold out on several different nights (a valid count on its own),
  // but summing across nights and dividing by one day's hotel count is
  // comparing two incompatible things. Fixed to reuse the same
  // average-of-per-night-percentages approach the (correct) "Avg.
  // Scarcity" summary card already uses, so the two numbers can never
  // disagree with each other again.
  const latestPct = avgScarcityForSnapshot(latest, stayDateColumns, windowDays);
  if (latestPct === null) return null;
  const firstNight = stayDateColumns.find((d) => latest.cells[d]);
  // "tonight" only when the nearest night really is the capture day; otherwise name the date
  const nightPhrase = windowDays === 1
    ? (firstNight === latest.asOfDate ? "tonight" : `the ${formatShort(firstNight)} night`)
    : "the next 7 nights";

  // For a concrete, genuinely comparable "X of Y" example (rather than
  // a cross-night sum), anchor it to the single NEAREST stay date --
  // one night, one hotel count, no mixing.
  const nearestDate = stayDateColumns.find((d) => latest.cells[d]);
  const nearestCell = nearestDate ? latest.cells[nearestDate] : null;
  // that night's own hotel count (latest.propertyCount is just whichever date happened to be read last)
  const propertyCount = nearestCell ? nearestCell.totalBeds : null;

  let trendPhrase = null;
  if (comparison) {
    const compPct = avgScarcityForSnapshot(comparison.snapshot, stayDateColumns, windowDays);
    if (compPct !== null) {
      const delta = latestPct - compPct;
      const direction = delta > 0.5 ? "tighter" : delta < -0.5 ? "looser" : "about the same as";
      trendPhrase = delta > 0.5 || delta < -0.5
        ? `That's ${direction} than it was ${comparison.actualDaysAgo} day${comparison.actualDaysAgo === 1 ? "" : "s"} ago (${compPct.toFixed(0)}%).`
        : `That's ${direction} ${comparison.actualDaysAgo} day${comparison.actualDaysAgo === 1 ? "" : "s"} ago.`;
    }
  }

  return (
    <div className="ap-headline">
      <div className="ap-headline-text">
        As of {formatShort(latest.asOfDate)}, {location} hotels are{" "}
        <strong>{latestPct.toFixed(0)}%</strong> sold out {windowDays === 1 ? "for" : "on average across"} {nightPhrase}.
        {windowDays > 1 && typeof nearestCell?.soldOutPropertiesEp === "number" && typeof propertyCount === "number" && (
          <> For the nearest night alone, that's {formatBeds(nearestCell.soldOutPropertiesEp)} of{" "}
          {formatBeds(propertyCount)} hotels.</>
        )}
        {trendPhrase && <> {trendPhrase}</>}
      </div>
    </div>
  );
}


export default function AgodaPickupDashboard() {
  const [platform] = useState(PLATFORM_OPTIONS[0]);
  const [location, setLocation] = useState(LOCATION_OPTIONS[0]);
  const latestLocation = useRef(location); // lets a slow answer for the PREVIOUS location be ignored
  latestLocation.current = location;
  const [displayMode, setDisplayMode] = useState("both"); // "both" | "pct" | "beds"
  const [selectedStayDate, setSelectedStayDate] = useState(null);

  const [liveRows, setLiveRows] = useState(null); // raw API rows; null = not loaded yet
  const [fromStayDate, setFromStayDate] = useState(DEFAULT_FROM_STAY_DATE);
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS); // 1 = today only, 7 = next 7 nights
  const [loadingLive, setLoadingLive] = useState(true);
  const [loadError, setLoadError] = useState(null); // string message, or null

  function loadLiveData(forceRefresh = false) {
    const requestedLocation = location;
    setLoadingLive(true);
    setLoadError(null);
    fetch(`/api/agodapickup/summary?location=${encodeURIComponent(requestedLocation)}${forceRefresh ? "&forceRefresh=true" : ""}`)
      .then((res) => res.json())
      .then((json) => {
        if (latestLocation.current !== requestedLocation) return; // the user switched location meanwhile
        if (json.success && Array.isArray(json.data)) {
          // An empty array is a legitimate, honest state (route works,
          // nothing captured yet) -- NOT the same as a failed request.
          setLiveRows(json.data);
        } else {
          setLiveRows([]);
          setLoadError(json.error || "The server returned an unexpected response.");
        }
      })
      .catch((err) => {
        if (latestLocation.current !== requestedLocation) return;
        setLiveRows([]);
        setLoadError(err.message || "Could not reach the server.");
      })
      .finally(() => {
        if (latestLocation.current === requestedLocation) setLoadingLive(false);
      });
  }

  useEffect(() => {
    setLiveRows(null); // never show the previous location's rows under the new name
    setSelectedStayDate(null);
    loadLiveData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const snapshots = useMemo(
    () => buildSnapshotsFromRows(filterRowsFromStayDate(liveRows || [], fromStayDate)),
    [liveRows, fromStayDate]
  );
  const hasData = snapshots.length > 0;
  const hasAnyRows = (liveRows || []).length > 0; // data exists, even if the date filter hides all of it

  const stayDateColumns = useMemo(() => {
    const set = new Set();
    snapshots.forEach((s) => Object.keys(s.cells).forEach((d) => set.add(d)));
    return Array.from(set).sort();
  }, [snapshots]);

  // Hotels listed per stay date, taken from the newest capture that has that
  // night. Snapshots run oldest -> newest, so walk backwards and keep the first hit.
  const hotelsByDate = useMemo(() => {
    const out = {};
    for (let i = snapshots.length - 1; i >= 0; i--) {
      const snap = snapshots[i];
      Object.keys(snap.cells).forEach((d) => {
        if (out[d] === undefined) out[d] = { count: snap.cells[d].totalBeds, claimed: snap.cells[d].claimed, asOf: snap.asOfLabel };
      });
    }
    return out;
  }, [snapshots]);

  // The highlighted column (and the pickup trend beside the table) starts on TODAY instead of the earliest date.
  // If today isn't in the table, the nearest upcoming date is used; if every date is in the past, the latest one.
  // Clicking a date still overrides this.
  const defaultStayDate = useMemo(() => {
    if (stayDateColumns.length === 0) return undefined;
    const today = toISO(new Date());
    return stayDateColumns.find((d) => d >= today) || stayDateColumns[stayDateColumns.length - 1];
  }, [stayDateColumns]);
  const activeStayDate = selectedStayDate && stayDateColumns.includes(selectedStayDate)
    ? selectedStayDate
    : defaultStayDate;

  // Scroll the table so that column sits right beside the pinned first column. Runs once per load, location
  // switch or "Stay dates from" change -- never when you click a date, so clicking doesn't make the table jump.
  const matrixScrollRef = React.useRef(null);
  const scrolledForRef = React.useRef(null);
  useEffect(() => {
    const scroller = matrixScrollRef.current;
    if (!scroller || !defaultStayDate) return;
    const key = `${location}|${fromStayDate}|${defaultStayDate}`;
    if (scrolledForRef.current === key) return;
    const headerCells = scroller.querySelectorAll("thead tr:first-child th");
    const target = headerCells[stayDateColumns.indexOf(defaultStayDate) + 1]; // +1: the first cell is the pinned label column
    if (!target || !headerCells[0]) return;
    scrolledForRef.current = key;
    // measured against the scroll area itself (a sticky header cell reports its offsetLeft from the page, not the table)
    const inside = target.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
    scroller.scrollLeft = Math.max(0, inside - headerCells[0].getBoundingClientRect().width);
  }, [defaultStayDate, stayDateColumns, location, fromStayDate]);

  const latest = snapshots[snapshots.length - 1];
  // The stay-date columns the summary cards cover: the nearest `windowDays`
  // nights that exist in the latest capture.
  const windowCols = useMemo(() => {
    if (!latest) return [];
    return stayDateColumns.filter((d) => latest.cells[d]).slice(0, windowDays);
  }, [latest, stayDateColumns, windowDays]);

  const winAvg = useMemo(() => {
    if (!latest || windowCols.length === 0) return null;
    return windowCols.reduce((s, d) => s + latest.cells[d].occupancyPct, 0) / windowCols.length;
  }, [latest, windowCols]);

  // Hotels sold out on ANY rate (the older count, which also includes hotels whose page failed to load).
  const winSoldOut = useMemo(() => {
    if (!latest || windowCols.length === 0) return null;
    return windowCols.reduce((s, d) => s + latest.cells[d].occupiedBeds, 0);
  }, [latest, windowCols]);

  // Average hotels listed per night in the window (for today-only this is simply that night's count).
  const winHotels = useMemo(() => {
    if (!latest || windowCols.length === 0) return null;
    return Math.round(windowCols.reduce((s, d) => s + latest.cells[d].totalBeds, 0) / windowCols.length);
  }, [latest, windowCols]);

  // Room-level figures (room-only rate) exist only for nights that were scraped in
  // detail; listing-only nights carry null. Sum what exists, and report how many of
  // the window's nights it covers so a partial total can never pass as a full one.
  function roomMetric(field) {
    if (!latest) return { value: null, have: 0, total: 0 };
    const withData = windowCols.filter((d) => latest.cells[d][field] !== null && latest.cells[d][field] !== undefined);
    return {
      value: withData.length ? withData.reduce((s, d) => s + latest.cells[d][field], 0) : null,
      have: withData.length,
      total: windowCols.length,
    };
  }
  const winSoldOutEp = useMemo(() => roomMetric("soldOutPropertiesEp"), [latest, windowCols]); // eslint-disable-line react-hooks/exhaustive-deps
  // Rooms available over the window: real where the night was scraped in detail, an
  // estimate (listing rooms x ratio) where it wasn't. Counts say how much of the total is estimated.
  const winRooms = useMemo(() => {
    const out = { value: null, have: 0, total: windowCols.length, estimated: 0, low: 0 };
    if (!latest) return out;
    let sum = 0;
    windowCols.forEach((d) => {
      const info = roomsForCell(latest.cells[d], d, latest.asOfDate);
      if (!info) return;
      sum += info.value; out.have += 1;
      if (info.estimated) { out.estimated += 1; if (info.lowConfidence) out.low += 1; }
    });
    out.value = out.have ? sum : null;
    return out;
  }, [latest, windowCols]);
  function roomsSub() {
    const m = winRooms;
    if (m.have === 0) return windowDays === 1 ? "No room data for this night yet" : "No room data for these nights yet";
    const partial = m.have < m.total ? `Room data for ${m.have} of ${m.total} nights only. ` : "";
    if (m.estimated === 0) return partial ? partial.trim() : "Room-only rate, cheapest option per hotel";
    if (m.estimated === m.have) return partial + (m.low > 0 ? "Estimate -- less certain this close to the night" : "Estimated from the listing");
    return `${partial}Includes ${m.estimated} estimated night${m.estimated === 1 ? "" : "s"}`;
  }
  // Rooms available per stay date for the header row: the newest capture that has a real or estimated figure.
  const roomsByDate = useMemo(() => {
    const out = {};
    for (let i = snapshots.length - 1; i >= 0; i--) {
      const snap = snapshots[i];
      Object.keys(snap.cells).forEach((d) => {
        if (out[d] !== undefined) return;
        const info = roomsForCell(snap.cells[d], d, snap.asOfDate);
        if (info) out[d] = { ...info, asOf: snap.asOfLabel };
      });
    }
    return out;
  }, [snapshots]);

  // "Max Occupancy of Market" = the most rooms open on any one night in ANY capture we hold (real count
  // or estimate), not just each night's newest figure. A less certain estimate (a night under
  // ROOMS_LOW_CONFIDENCE_DAYS away, where the 2.49 ratio is looser and can overstate) is left out so
  // it can't set the record; real counts always count. On a tie a real count beats an estimate.
  const marketMax = useMemo(() => {
    let best = null;
    let cellsChecked = 0;
    snapshots.forEach((snap) => {
      Object.keys(snap.cells).forEach((d) => {
        const info = roomsForCell(snap.cells[d], d, snap.asOfDate);
        if (!info) return;
        cellsChecked += 1;
        if (info.estimated && info.lowConfidence) return;
        const better = best === null || info.value > best.value
          || (info.value === best.value && best.estimated && !info.estimated);
        if (better) best = { value: info.value, stayDate: d, estimated: info.estimated, asOf: snap.asOfLabel, asOfDate: snap.asOfDate };
      });
    });
    return best ? { ...best, cellsChecked, captures: snapshots.length } : null;
  }, [snapshots]);
  // "Top nights by rooms available": for every stay date, its highest rooms-available figure from any
  // capture (a real count, or an estimate), then the TOP_NIGHTS nights with the most rooms, highest first.
  // Same rules as the Max Occupancy card, so the first row is always that card's figure: a less certain
  // estimate (a night under ROOMS_LOW_CONFIDENCE_DAYS away) is left out; on a tie a real count beats an
  // estimate, and between two equal figures the newer capture is shown.
  const topRoomNights = useMemo(() => {
    const bestByNight = new Map();
    snapshots.forEach((snap) => {
      Object.keys(snap.cells).forEach((d) => {
        const info = roomsForCell(snap.cells[d], d, snap.asOfDate);
        if (!info) return;
        if (info.estimated && info.lowConfidence) return;
        const cur = bestByNight.get(d);
        const better = !cur
          || info.value > cur.value
          || (info.value === cur.value && cur.estimated && !info.estimated)
          || (info.value === cur.value && cur.estimated === info.estimated && snap.asOfDate > cur.asOfDate);
        if (better) {
          bestByNight.set(d, { stayDate: d, value: info.value, estimated: info.estimated, listing: info.listing, asOf: snap.asOfLabel, asOfDate: snap.asOfDate });
        }
      });
    });
    return Array.from(bestByNight.values())
      .sort((a, b) => b.value - a.value || a.stayDate.localeCompare(b.stayDate))
      .slice(0, TOP_NIGHTS);
  }, [snapshots]);
  function roomSub(metric, fullText) {
    if (metric.have === 0) return windowDays === 1 ? "No room-level data for this night yet" : "No room-level data for these nights yet";
    if (metric.have < metric.total) return `Room data for ${metric.have} of ${metric.total} nights only`;
    return fullText;
  }
  // Card wording that follows the window
  const nightLabel = windowCols.length === 0 ? "" : formatShort(windowCols[0]);
  const windowLabel = windowDays === 1
    ? (windowCols[0] && windowCols[0] === latest?.asOfDate ? `Today (${nightLabel})` : nightLabel)
    : "Next 7 Days";
  // Finds a real "N days ago" capture to compare the headline against --
  // may not be exactly 7 days back if that date wasn't captured, in
  // which case the headline says how many days back it actually found.
  const weekAgoComparison = useMemo(() => {
    if (!latest) return null;
    return findComparisonSnapshot(snapshots, latest.asOfDate, 7);
  }, [latest, snapshots]);

  // The fixed bottom row: how each night's SOLD ROOMS compare with the night BEFORE it:
  //   (sold on this date - sold on the date before) / sold on the date before
  // Each night's sold rooms = market maximum - its newest rooms-available figure (the same figure the
  // ROOMS AVAILABLE (LATEST) row shows, which may come from an earlier capture for nights already gone).
  const varianceByDate = useMemo(() => {
    const out = {};
    if (!marketMax) return out;
    const soldOn = (d) => {
      const info = roomsByDate[d];
      return info ? { sold: Math.max(0, marketMax.value - info.value), available: info.value, estimated: info.estimated, asOf: info.asOf } : null;
    };
    stayDateColumns.forEach((d) => {
      const prevDate = addDays(d, -1);
      const now = soldOn(d);
      const then = soldOn(prevDate);
      if (!now) { out[d] = { status: "none" }; return; }
      if (!then) { out[d] = { status: "no-prev", prevDate }; return; }
      if (then.sold === 0) { out[d] = { status: "from-zero", prevDate, now, then }; return; }
      const pct = ((now.sold - then.sold) / then.sold) * 100;
      out[d] = { status: pct === 0 ? "flat" : pct > 0 ? "up" : "down", pct, prevDate, now, then };
    });
    return out;
  }, [roomsByDate, marketMax, stayDateColumns]);

  return (
    <div className="ap-root">
      <HeadlineInsight latest={latest} comparison={weekAgoComparison} stayDateColumns={stayDateColumns} windowDays={windowDays} location={location} />
      <div className="ap-filter-bar">
        <div className="ap-filter-row">
          <div className="ap-filter-group">
            <label className="ap-filter-label">Platform</label>
            <select className="ap-select" value={platform} disabled>
              {PLATFORM_OPTIONS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="ap-filter-group">
            <label className="ap-filter-label">Location</label>
            <select className="ap-select" value={location} onChange={(e) => setLocation(e.target.value)}>
              {LOCATION_OPTIONS.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </div>
          <div className="ap-filter-group">
            <label className="ap-filter-label">Stay dates from</label>
            <input
              type="date"
              className="ap-select"
              value={fromStayDate}
              onChange={(e) => setFromStayDate(e.target.value)}
              title="Hide stay dates before this day. Clear the box to show everything."
            />
          </div>
          <div className="ap-filter-group">
            <label className="ap-filter-label">Cards cover</label>
            <select
              className="ap-select"
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              title="What the summary cards and the headline describe"
            >
              <option value={1}>Today only</option>
              <option value={7}>Next 7 days</option>
            </select>
          </div>
          <div className="ap-filter-group ap-display-toggle-group">
            <label className="ap-filter-label">Show</label>
            <div className="ap-toggle-group">
              {[
                { key: "both", label: "Sold Out + %" },
                { key: "pct", label: "% only" },
                { key: "beds", label: "Sold Out only" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  className={`ap-toggle-btn${displayMode === opt.key ? " ap-toggle-btn-active" : ""}`}
                  onClick={() => setDisplayMode(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <button
            className="ap-btn-regenerate"
            onClick={() => loadLiveData(true)}
            disabled={loadingLive}
            title="Re-fetch the latest Athena data, bypassing the cache"
          >
            <RefreshCw size={14} className={loadingLive ? "ap-spin" : ""} />
            {loadingLive ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        <div className={`ap-data-banner ${hasData ? "ap-data-banner-live" : loadError ? "ap-data-banner-error" : "ap-data-banner-loading"}`}>
          {loadingLive
            ? "Loading live data from the Agoda pickup Athena table…"
            : hasData
            ? `Live ${location} data from the Agoda pickup Athena table${fromStayDate ? `, stay dates from ${formatShort(fromStayDate)}` : ""}.${location !== "Rishikesh" ? " Hotel counts and sold-out % are exact; room numbers are estimates that use Rishikesh's ratio until this location is calibrated." : ""}`
            : loadError
            ? `Couldn't load live data: ${loadError}`
            : hasAnyRows
            ? `No captures have stay dates on or after ${fromStayDate}. Change "Stay dates from" to see older data.`
            : "No captures yet. Run the scraper and upload a summary to see data here."}
        </div>
      </div>

      <div className="ap-summary-row">
        <SummaryCard
          label={`Avg. Scarcity, ${windowLabel}`}
          value={winAvg !== null ? `${winAvg.toFixed(1)}%` : "—"}
          sub="Share of hotels sold out, latest capture"
          tooltip={windowDays === 1
            ? "How full the market is for the night shown. Higher means fewer rooms left to book."
            : "How full the market is across the next 7 stay dates, averaged. Higher means fewer rooms left to book."}
        />
        <SummaryCard
          label={`Hotels Sold Out, ${windowLabel}`}
          value={winSoldOutEp.value !== null ? formatBeds(winSoldOutEp.value) : "—"}
          sub={roomSub(winSoldOutEp, "Room-only rate, successful checks only")}
          tooltip="Hotels with zero room-only (no breakfast) rooms left. This is the trustworthy count -- it excludes hotels whose page simply failed to load. It needs room-level data, which comes from a detail scrape of that night."
        />
        <SummaryCard
          label={`Rooms Still Available, ${windowLabel}`}
          value={winRooms.value !== null ? `${winRooms.estimated > 0 ? "~" : ""}${formatBeds(winRooms.value)}` : "—"}
          sub={roomsSub()}
          tooltip={`Rooms left at the room-only (no breakfast) rate. For a night scraped in detail this is a real count. For other nights it is an ESTIMATE: the rooms-left total the listing shows, times ${ROOMS_ESTIMATE_RATIO} (measured on 4 nights 7-31 days out, within about 1-2%; less certain for nights under ${ROOMS_LOW_CONFIDENCE_DAYS} days away, and unchecked beyond 31 days).`}
        />
        <SummaryCard
          label="Max Occupancy of Market"
          value={marketMax ? `${marketMax.estimated ? "~" : ""}${formatBeds(marketMax.value)}` : "—"}
          sub={marketMax
            ? `Most rooms open on one night, any capture: ${formatShort(marketMax.stayDate)} (captured ${formatShort(marketMax.asOfDate)})${marketMax.estimated ? " -- estimated" : ""}`
            : "No room figures yet"}
          tooltip={marketMax
            ? `The highest rooms-available figure in the whole table: ${formatBeds(marketMax.value)} rooms for the night of ${formatShort(marketMax.stayDate)}, from the capture of ${marketMax.asOf}${marketMax.estimated ? ` (estimated from the listing, rooms x ${ROOMS_ESTIMATE_RATIO})` : " (counted from every hotel's page)"}. Searched ${formatBeds(marketMax.cellsChecked)} night figures across ${marketMax.captures} captures. Estimates for nights under ${ROOMS_LOW_CONFIDENCE_DAYS} days away are left out (less certain). The emptiest night is the one least reduced by bookings, so it is a rough guide to the market's total room capacity. It follows "Stay dates from", not "Cards cover".`
            : "Needs at least one night with a rooms figure."}
        />
        <SummaryCard
          label="Hotels Tracked"
          value={winHotels !== null ? formatBeds(winHotels) : "—"}
          sub={windowDays === 1 ? "Hotels listed for this night" : "Average per night, next 7 nights"}
          tooltip="How many hotels were listed for the night(s) above in the latest capture. Scarcity and sold-out figures are calculated against this count."
        />
        <SummaryCard
          label="Last Captured"
          value={latest ? formatShort(latest.asOfDate) : "—"}
          sub=""
          tooltip="The date this data was collected."
        />
        <SummaryCard
          label={`Sold Out or Unreachable, ${windowLabel}`}
          value={winSoldOut !== null ? formatBeds(winSoldOut) : "—"}
          sub="Older metric -- see tooltip"
          tooltip="Legacy figure: includes genuinely sold-out hotels AND hotels whose page failed to load that day, lumped together. Prefer 'Hotels Sold Out' above for a trustworthy number."
        />
      </div>
      <p className="ap-summary-footnote">
        {windowDays === 1
          ? "The cards describe the nearest night in the latest capture (normally today). Use \"Cards cover\" above to switch to the next 7 nights."
          : "\"Next 7 Days\" means the 7 nearest upcoming stay dates visible in the table below, not always a fixed calendar week."}
      </p>

      <div className="ap-body">
        <div className="ap-matrix-card">
          <div className="ap-matrix-scroll" ref={matrixScrollRef}>
            <table className="ap-table">
              <thead>
                <tr>
                  <th className="ap-th-fixed">CAPTURED (AS OF)</th>
                  {stayDateColumns.map((d) => (
                    <th
                      key={d}
                      className={`ap-th-date${d === activeStayDate ? " ap-th-date-active" : ""}`}
                      onClick={() => setSelectedStayDate(d)}
                      title="Click to see the pickup trend for this stay date"
                    >
                      {formatWithWeekday(d)}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th className="ap-th2-fixed" title="Hotels listed for each night, from the newest capture that includes that night">
                    HOTELS LISTED (LATEST)
                  </th>
                  {stayDateColumns.map((d) => {
                    const info = hotelsByDate[d];
                    const thin = info && typeof info.count === "number" && info.count < THIN_HOTEL_COUNT;
                    // share of the hotels Agoda says exist that this count covers (when the capture recorded Agoda's total)
                    const cov = info && typeof info.count === "number" && typeof info.claimed === "number" && info.claimed > 0
                      ? info.count / info.claimed : null;
                    return (
                      <th
                        key={d}
                        className={`ap-th2-date${d === activeStayDate ? " ap-th2-date-active" : ""}${thin ? " ap-th2-thin" : ""}`}
                        onClick={() => setSelectedStayDate(d)}
                        title={info
                          ? `${formatBeds(info.count)} hotels listed for ${formatShort(d)} in the capture of ${info.asOf}${cov !== null ? `. Agoda says ${formatBeds(info.claimed)} exist for this night, so this covers ${Math.round(cov * 100)}% of its list${cov < COVERAGE_WARN ? " -- lower than usual, the night's figures may be skewed" : ""}` : ""}${thin ? ` -- fewer than ${THIN_HOTEL_COUNT}, so this night's sample looks incomplete` : ""}`
                          : "No data for this night"}
                      >
                        {info ? formatBeds(info.count) : "—"}
                        {cov !== null && (
                          <span className={`ap-th2-cov${cov < COVERAGE_WARN ? " ap-th2-cov-low" : ""}`}>{Math.round(cov * 100)}%</span>
                        )}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  <th className="ap-th3-fixed" title="Rooms left at the room-only rate for each night. A plain number is a real count (night scraped in detail); a number starting with ~ is an estimate from the listing.">
                    ROOMS AVAILABLE (LATEST)
                  </th>
                  {stayDateColumns.map((d) => {
                    const info = roomsByDate[d];
                    const cls = `ap-th3-date${d === activeStayDate ? " ap-th3-date-active" : ""}${info && info.estimated ? " ap-th3-est" : ""}${info && info.lowConfidence ? " ap-th3-low" : ""}`;
                    let title = "No room figure for this night yet";
                    if (info && !info.estimated) {
                      title = `${formatBeds(info.value)} rooms left at the room-only rate, counted from every hotel's page (capture of ${info.asOf})`;
                    } else if (info) {
                      title = `About ${formatBeds(info.value)} rooms: the listing shows ${formatBeds(info.listing)} rooms left x ${ROOMS_ESTIMATE_RATIO} (ratio measured on nights 7-31 days out) -- an estimate, capture of ${info.asOf}.${info.lowConfidence ? ` Less certain for nights under ${ROOMS_LOW_CONFIDENCE_DAYS} days away.` : ""}`;
                    }
                    return (
                      <th key={d} className={cls} onClick={() => setSelectedStayDate(d)} title={title}>
                        {info ? `${info.estimated ? "~" : ""}${formatBeds(info.value)}` : "—"}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {snapshots.map((row, rIdx) => {
                  const rowBg = rIdx % 2 === 0 ? "ap-row-even" : "ap-row-odd";
                  return (
                    <tr key={row.asOfDate} className={rowBg}>
                      <td className={`ap-td-fixed ${rowBg}`}>
                        {row.asOfLabel}
                        <span className="ap-td-fixed-sub">
                          {row.propertyCount} properties
                        </span>
                      </td>
                      {stayDateColumns.map((d) => {
                        const cell = row.cells[d];
                        const isActiveCol = d === activeStayDate;
                        if (!cell) {
                          return (
                            <td key={d} className={`ap-cell-empty${isActiveCol ? " ap-col-active" : ""}`}>
                              —
                            </td>
                          );
                        }
                        // This capture's own rooms figure, and what is sold against the market maximum.
                        const sold = soldForCell(cell, d, row.asOfDate, marketMax ? marketMax.value : null);
                        return (
                          <td
                            key={d}
                            className={`ap-td-cell ${occupancyClass(sold ? sold.pct : cell.occupancyPct)}${
                              isActiveCol ? " ap-col-active" : ""
                            }`}
                          >
                            {sold ? (
                              <>
                                <span
                                  className={`ap-cell-avail${sold.estimated ? " ap-cell-avail-est" : ""}${sold.lowConfidence ? " ap-cell-avail-low" : ""}`}
                                  title={sold.estimated
                                    ? `About ${formatBeds(sold.available)} rooms left at the room-only rate in the capture of ${row.asOfLabel}: the listing showed ${formatBeds(sold.listing)} x ${ROOMS_ESTIMATE_RATIO}. An estimate.${sold.lowConfidence ? ` Less certain for nights under ${ROOMS_LOW_CONFIDENCE_DAYS} days away.` : ""}`
                                    : `${formatBeds(sold.available)} rooms left at the room-only rate in the capture of ${row.asOfLabel}, counted from every hotel's page.`}
                                >
                                  {sold.estimated ? "~" : ""}{formatBeds(sold.available)} avail.
                                </span>
                                {(displayMode === "both" || displayMode === "beds") && (
                                  <span
                                    className="ap-cell-beds"
                                    title={`Sold rooms = market maximum ${formatBeds(sold.max)} - rooms available ${formatBeds(sold.available)}${sold.overMax ? ". The available figure is above the maximum (a less certain estimate), so sold is shown as 0." : "."}`}
                                  >
                                    {formatBeds(sold.sold)} sold
                                  </span>
                                )}
                                {(displayMode === "both" || displayMode === "pct") && (
                                  <span className="ap-cell-pct" title={`Sold % = sold rooms ${formatBeds(sold.sold)} / market maximum ${formatBeds(sold.max)}`}>
                                    {sold.pct.toFixed(1)}%
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="ap-cell-na" title="This capture has no rooms figure for this night (it predates the listing rooms data, or no maximum exists yet). The figures below count hotels, not rooms.">rooms n/a</span>
                                {(displayMode === "both" || displayMode === "beds") && (
                                  <span className="ap-cell-beds">{formatBeds(cell.occupiedBeds)}</span>
                                )}
                                {(displayMode === "both" || displayMode === "pct") && (
                                  <span className="ap-cell-pct">{cell.occupancyPct.toFixed(1)}%</span>
                                )}
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th
                    className="ap-tf-fixed"
                    title="How each night's SOLD ROOMS compare with the night before it: (sold on this date - sold on the date before) / sold on the date before. Sold rooms = market maximum - rooms available, each from the newest capture that has that night."
                  >
                    VARIANCE % (VS PREVIOUS DATE)
                  </th>
                  {stayDateColumns.map((d) => {
                    const v = varianceByDate[d];
                    let label = "—";
                    let cls = "ap-tf-none";
                    let title = "No rooms figure for this night yet";
                    if (v && v.status === "no-prev") { title = `No rooms figure for the night before (${formatShort(v.prevDate)}), so there is nothing to compare with`; }
                    else if (v && v.status === "from-zero") { title = `Sold rooms were 0 on ${formatShort(v.prevDate)}, so a % change can't be worked out (${formatShort(d)} has ${formatBeds(v.now.sold)} sold)`; }
                    else if (v && v.now) {
                      const delta = v.now.sold - v.then.sold;
                      const mixed = v.now.estimated || v.then.estimated;
                      label = v.status === "flat" ? "0.0%" : `${v.status === "up" ? "▲" : "▼"} ${Math.abs(v.pct).toFixed(1)}%`;
                      cls = v.status === "up" ? "ap-tf-up" : v.status === "down" ? "ap-tf-down" : "ap-tf-flat";
                      title = `Sold rooms ${formatBeds(v.now.sold)} for ${formatShort(d)} vs ${formatBeds(v.then.sold)} for ${formatShort(v.prevDate)} (${delta >= 0 ? "+" : ""}${formatBeds(delta)} rooms, ${v.pct >= 0 ? "+" : ""}${v.pct.toFixed(1)}%). Each is that night's newest figure (captures of ${v.now.asOf} and ${v.then.asOf}).${mixed ? " Built from estimated room figures." : ""}`;
                    }
                    return (
                      <th key={d} className={`ap-tf-date ${cls}${d === activeStayDate ? " ap-tf-date-active" : ""}`} onClick={() => setSelectedStayDate(d)} title={title}>
                        {label}
                      </th>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="ap-side-panel">
          <div className="ap-top-card">
            <div className="ap-top-title">Top {TOP_NIGHTS} nights by rooms available</div>
            <div className="ap-top-sub">Each night's highest figure from any capture, most rooms first.</div>
            {topRoomNights.length === 0 ? (
              <div className="ap-top-empty">No room figures yet.</div>
            ) : (
              <table className="ap-top-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Stay date</th>
                    <th className="ap-top-num">Rooms available</th>
                    <th>Captured</th>
                  </tr>
                </thead>
                <tbody>
                  {topRoomNights.map((r, i) => (
                    <tr
                      key={r.stayDate}
                      className={`ap-top-row${r.stayDate === activeStayDate ? " ap-top-row-active" : ""}`}
                      onClick={() => setSelectedStayDate(r.stayDate)}
                      title={r.estimated
                        ? `About ${formatBeds(r.value)} rooms for ${formatWithWeekday(r.stayDate)}: the listing showed ${formatBeds(r.listing)} rooms left x ${ROOMS_ESTIMATE_RATIO}. An estimate, from the capture of ${r.asOf}. Click to see this night's pickup trend.`
                        : `${formatBeds(r.value)} rooms for ${formatWithWeekday(r.stayDate)}, counted from every hotel's page in the capture of ${r.asOf}. Click to see this night's pickup trend.`}
                    >
                      <td className="ap-top-rank">{i + 1}</td>
                      <td>{formatWithWeekday(r.stayDate)}</td>
                      <td className={`ap-top-num${r.estimated ? " ap-top-est" : ""}`}>{r.estimated ? "~" : ""}{formatBeds(r.value)}</td>
                      <td>{formatShort(r.asOfDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {activeStayDate && (
            <div className="ap-trend-card">
              <div className="ap-trend-card-heading">
                <TrendingUp size={14} /> Pickup trend
              </div>
              <TrendPanel stayDate={activeStayDate} snapshots={snapshots} />
            </div>
          )}
        </div>
      </div>

      {/* ---- styles kept in this same file, scoped with the ap- prefix ---- */}
      <style>{`
        .ap-root {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #1e293b;
        }
        .ap-filter-bar {
          background: #fff;
          border-bottom: 1px solid #e2e8f0;
          padding: 16px 24px;
          flex-shrink: 0;
        }
        .ap-filter-row {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 16px;
        }
        .ap-filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 160px;
        }
        .ap-filter-label {
          font-size: 11px;
          font-weight: 500;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .ap-select {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 8px 10px;
          font-size: 13.5px;
          color: #1e293b;
          background: #fff;
          cursor: pointer;
        }
        .ap-select:disabled {
          background: #f1f5f9;
          color: #94a3b8;
          cursor: not-allowed;
        }
        .ap-select:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(30, 42, 138, 0.25);
        }
        .ap-display-toggle-group {
          width: auto;
        }
        .ap-toggle-group {
          display: flex;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
        }
        .ap-toggle-btn {
          padding: 8px 12px;
          font-size: 12.5px;
          font-weight: 500;
          color: #475569;
          background: #fff;
          border: none;
          border-right: 1px solid #e2e8f0;
          cursor: pointer;
          white-space: nowrap;
        }
        .ap-toggle-btn:last-child { border-right: none; }
        .ap-toggle-btn:hover { background: #f8fafc; }
        .ap-toggle-btn-active { background: #1e2a8a; color: #fff; }
        .ap-btn-regenerate {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 500;
          border-radius: 6px;
          border: 1px solid #bfdbfe;
          background: #eff6ff;
          color: #1d4ed8;
          cursor: pointer;
          margin-left: auto;
          white-space: nowrap;
        }
        .ap-btn-regenerate:hover:not(:disabled) { background: #dbeafe; }
        .ap-btn-regenerate:disabled { opacity: 0.7; cursor: not-allowed; }
        .ap-spin { animation: ap-spin-anim 1s linear infinite; }
        @keyframes ap-spin-anim { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .ap-data-banner {
          margin-top: 12px;
          font-size: 11.5px;
          font-weight: 500;
          padding: 6px 10px;
          border-radius: 6px;
          display: inline-block;
        }
        .ap-data-banner-live { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
        .ap-data-banner-loading { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
        .ap-data-banner-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
        .ap-headline {
          flex-shrink: 0;
          background: #1e2a8a;
          padding: 18px 24px;
        }
        .ap-headline-text {
          font-size: 16px;
          line-height: 1.5;
          color: #e0e7ff;
          max-width: 760px;
        }
        .ap-headline-text strong { color: #fff; font-size: 19px; }
        .ap-summary-footnote {
          margin: 6px 24px 0;
          font-size: 11px;
          color: #94a3b8;
          flex-shrink: 0;
        }
        .ap-summary-row {
          display: flex;
          gap: 14px;
          padding: 16px 24px 0;
          flex-shrink: 0;
          flex-wrap: wrap;
        }
        .ap-summary-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 14px 18px;
          min-width: 160px;
          flex: 1 1 160px;
        }
        .ap-summary-value { font-size: 22px; font-weight: 700; color: #1e2a8a; line-height: 1.15; }
        .ap-summary-label {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          margin-top: 4px;
        }
        .ap-summary-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
        .ap-body {
          flex: 1;
          min-height: 0;
          display: flex;
          gap: 16px;
          padding: 16px 24px 20px;
          overflow: hidden;
        }
        .ap-matrix-card {
          flex: 1;
          min-width: 0;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .ap-matrix-scroll { flex: 1; min-height: 0; overflow: auto; }
        .ap-side-panel {
          width: 300px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 14px;
          overflow-y: auto;
        }
        .ap-table { border-collapse: collapse; width: 100%; font-size: 13px; }
        .ap-th-fixed {
          position: sticky;
          top: 0;
          left: 0;
          z-index: 30;
          background: #1e2a8a;
          color: #fff;
          text-align: left;
          padding: 10px 16px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.02em;
          white-space: nowrap;
          min-width: 200px;
          box-shadow: 2px 0 0 0 #1e2a8a;
        }
        .ap-th-date {
          position: sticky;
          top: 0;
          z-index: 20;
          background: #1e293b;
          color: #e2e8f0;
          text-align: center;
          padding: 10px 6px;
          font-size: 11px;
          font-weight: 600;
          border-right: 1px solid #334155;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
          min-width: 108px;
        }
        .ap-th-date:hover { background: #334155; }
        .ap-th-date-active { background: #3730a3; color: #fff; }
        /* first header row is a fixed 36px so the hotel-count row can stick directly beneath it */
        .ap-th-fixed, .ap-th-date { height: 36px; box-sizing: border-box; }
        .ap-th2-fixed {
          position: sticky;
          top: 36px;
          left: 0;
          z-index: 30;
          background: #172554;
          color: #c7d2fe;
          text-align: left;
          padding: 6px 16px;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.02em;
          white-space: nowrap;
          height: 32px;
          box-sizing: border-box;
          box-shadow: 2px 0 0 0 #172554;
          border-top: 1px solid #334155;
        }
        .ap-th2-date {
          position: sticky;
          top: 36px;
          z-index: 20;
          background: #273449;
          color: #f1f5f9;
          text-align: center;
          padding: 6px;
          font-size: 12.5px;
          font-weight: 700;
          border-right: 1px solid #334155;
          border-top: 1px solid #334155;
          height: 32px;
          box-sizing: border-box;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .ap-th2-date-active { background: #4338ca; }
        .ap-th2-thin { color: #fcd34d; }
        .ap-th3-fixed {
          position: sticky;
          top: 68px;
          left: 0;
          z-index: 30;
          background: #14213d;
          color: #bae6fd;
          text-align: left;
          padding: 6px 16px;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.02em;
          white-space: nowrap;
          height: 32px;
          box-sizing: border-box;
          box-shadow: 2px 0 0 0 #14213d;
          border-top: 1px solid #334155;
        }
        .ap-th3-date {
          position: sticky;
          top: 68px;
          z-index: 20;
          background: #1f3047;
          color: #f1f5f9;
          text-align: center;
          padding: 6px;
          font-size: 12.5px;
          font-weight: 700;
          border-right: 1px solid #334155;
          border-top: 1px solid #334155;
          height: 32px;
          box-sizing: border-box;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .ap-th3-date-active { background: #4338ca; }
        .ap-th3-est { font-style: italic; color: #bae6fd; font-weight: 600; }
        .ap-th3-low { text-decoration: underline dotted; text-underline-offset: 3px; }
        .ap-cell-avail { display: block; font-size: 11px; font-weight: 700; color: #334155; margin-bottom: 1px; }
        .ap-cell-avail-est { font-style: italic; font-weight: 600; }
        .ap-cell-avail-low { text-decoration: underline dotted; text-underline-offset: 3px; }
        .ap-cell-na { display: block; font-size: 10px; font-weight: 500; font-style: italic; color: #94a3b8; margin-bottom: 1px; }
        /* the variance row sticks to the bottom of the table's scroll area, so it stays visible while the capture rows scroll */
        .ap-tf-fixed {
          position: sticky;
          bottom: 0;
          left: 0;
          z-index: 30;
          background: #0b1224;
          color: #fde68a;
          text-align: left;
          padding: 8px 16px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.02em;
          white-space: nowrap;
          box-sizing: border-box;
          box-shadow: 2px 0 0 0 #0b1224;
          border-top: 2px solid #475569;
        }
        .ap-tf-date {
          position: sticky;
          bottom: 0;
          z-index: 20;
          background: #111c36;
          text-align: center;
          padding: 8px 6px;
          font-size: 12px;
          font-weight: 700;
          border-right: 1px solid #334155;
          border-top: 2px solid #475569;
          box-sizing: border-box;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .ap-tf-date-active { background: #312e81; }
        .ap-tf-up { color: #6ee7b7; }
        .ap-tf-down { color: #fca5a5; }
        .ap-tf-flat { color: #cbd5e1; }
        .ap-tf-none { color: #64748b; font-weight: 500; }
        .ap-th2-cov { margin-left: 6px; font-size: 9.5px; font-weight: 600; color: #94a3b8; }
        .ap-th2-cov-low { color: #fcd34d; }
        .ap-row-even { background: #fff; }
        .ap-row-odd { background: #f8fafc; }
        .ap-td-fixed {
          position: sticky;
          left: 0;
          z-index: 10;
          padding: 10px 16px;
          font-weight: 600;
          color: #1e293b;
          border-right: 1px solid #e2e8f0;
          border-bottom: 1px solid #f1f5f9;
          white-space: nowrap;
        }
        .ap-td-fixed-sub { display: block; font-size: 10.5px; font-weight: 400; color: #94a3b8; margin-top: 2px; }
        .ap-td-cell {
          text-align: center;
          padding: 8px 6px;
          border-right: 1px solid rgba(255, 255, 255, 0.5);
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        .ap-cell-empty {
          text-align: center;
          padding: 8px 6px;
          color: #cbd5e1;
          border-right: 1px solid #f1f5f9;
          border-bottom: 1px solid #f1f5f9;
        }
        .ap-col-active { box-shadow: inset 0 0 0 2px #3730a3; }
        .ap-cell-beds { display: block; font-weight: 700; font-size: 13px; }
        .ap-cell-pct { display: block; font-size: 11px; font-weight: 500; opacity: 0.85; }
        .ap-cell-high { background: #bbf7d0; color: #14532d; }
        .ap-cell-mid { background: #fef08a; color: #713f12; }
        .ap-cell-low { background: #fecaca; color: #7f1d1d; }
        .ap-variance { display: block; margin-top: 4px; font-size: 9.5px; font-weight: 700; white-space: nowrap; }
        .ap-variance-up { color: #065f46; }
        .ap-variance-down { color: #b91c1c; }
        .ap-variance-flat { color: #64748b; font-weight: 500; }
        .ap-variance-none { color: #94a3b8; font-weight: 500; font-style: italic; }
        .ap-trend-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
        .ap-trend-card-heading {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12.5px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 10px;
        }
        .ap-trend-empty { font-size: 12px; color: #94a3b8; }
        .ap-trend-header { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
        .ap-trend-title { font-size: 13px; font-weight: 700; color: #1e293b; }
        .ap-trend-subtitle { font-size: 11px; color: #64748b; }
        .ap-growth-badge {
          align-self: flex-start;
          font-size: 10.5px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 10px;
        }
        .ap-growth-up { background: #bbf7d0; color: #14532d; }
        .ap-growth-down { background: #fecaca; color: #7f1d1d; }
        .ap-trend-bars { display: flex; flex-direction: column; gap: 8px; }
        .ap-trend-row { display: grid; grid-template-columns: 88px 1fr 76px; align-items: center; gap: 8px; }
        .ap-trend-row-label {
          font-size: 10.5px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ap-trend-row-track { height: 14px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
        .ap-trend-row-fill { height: 100%; border-radius: 4px; min-width: 3px; transition: width 0.2s ease; }
        .ap-trend-row-value { font-size: 11px; font-weight: 700; color: #1e293b; text-align: right; }
        .ap-trend-row-beds { display: block; font-size: 9.5px; font-weight: 400; color: #94a3b8; }
        .ap-top-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; flex-shrink: 0; }
        .ap-top-title { font-size: 12.5px; font-weight: 700; color: #1e293b; }
        .ap-top-sub { font-size: 11px; color: #94a3b8; margin: 2px 0 10px; line-height: 1.4; }
        .ap-top-empty { font-size: 12px; color: #94a3b8; }
        .ap-top-table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
        .ap-top-table th {
          text-align: left;
          font-size: 9.5px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          padding: 0 4px 6px;
          border-bottom: 1px solid #e2e8f0;
        }
        .ap-top-table td { padding: 7px 4px; border-bottom: 1px solid #f1f5f9; color: #334155; white-space: nowrap; }
        .ap-top-num { text-align: right !important; font-variant-numeric: tabular-nums; }
        .ap-top-table td.ap-top-num { font-weight: 700; color: #1e2a8a; }
        .ap-top-est { font-style: italic; font-weight: 600 !important; }
        .ap-top-rank { color: #94a3b8; font-weight: 600; width: 18px; }
        .ap-top-row { cursor: pointer; }
        .ap-top-row:hover { background: #f8fafc; }
        .ap-top-row-active { background: #eef2ff; }
        .ap-top-row-active td { color: #1e2a8a; }
        @media (max-width: 900px) {
          .ap-body { flex-direction: column; overflow: auto; }
          .ap-side-panel { width: 100%; }
          .ap-matrix-card { max-height: 60vh; }
        }
        @media (max-width: 640px) {
          .ap-filter-row { gap: 10px; }
          .ap-filter-group { width: 100%; }
          .ap-btn-regenerate { margin-left: 0; width: 100%; justify-content: center; }
          .ap-summary-row { padding: 12px 14px 0; }
          .ap-body { padding: 12px 14px 16px; }
        }
      `}</style>
    </div>
  );
}
